export type ImportableUserRole = "gutachter" | "assistent";

export type CsvUserRow = {
  lineNumber: number;
  name: string;
  email: string;
  roleLabel: string;
  operationalArea: string;
};

export type OrganizationOption = {
  _id: string;
  name: string;
  slug?: string;
};

export type ImportRowReason =
  | "duplicate_email"
  | "existing_email"
  | "invalid_email"
  | "invalid_name"
  | "missing_area"
  | "missing_area_mapping"
  | "multiple_areas"
  | "unsupported_role";

export type ImportRowStatus = "ready" | "invalid" | "skipped";

export type ImportPreviewRow = {
  lineNumber: number;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
  role: ImportableUserRole | null;
  roleLabel: string;
  status: ImportRowStatus;
  reason: ImportRowReason | null;
  operationalArea: string;
  businessAreas: string[];
  organizationId: string | null;
  organizationName: string | null;
};

const REQUIRED_HEADERS = ["Name", "E-Mail Adresse", "Rolle", "Operativer Bereich"] as const;

function parseDelimitedLine(line: string) {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === "\"") {
      const next = line[i + 1];
      if (inQuotes && next === "\"") {
        current += "\"";
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ";" && !inQuotes) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
}

export function splitName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) {
    return null;
  }

  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1] ?? "",
  };
}

export function normalizeCsvRole(roleLabel: string): ImportableUserRole | null {
  const normalized = roleLabel.trim().toLowerCase();

  if (normalized === "assessor") {
    return "gutachter";
  }

  if (normalized === "assistant") {
    return "assistent";
  }

  return null;
}

export function splitBusinessAreas(raw: string) {
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function normalizeForLookup(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "")
    .toLowerCase();
}

export function parseUsersCsv(csvText: string): CsvUserRow[] {
  const lines = csvText
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return [];
  }

  const headerValues = parseDelimitedLine(lines[0]);
  const headerIndexes = Object.fromEntries(
    REQUIRED_HEADERS.map((header) => [header, headerValues.indexOf(header)])
  );

  for (const header of REQUIRED_HEADERS) {
    if (headerIndexes[header] === -1) {
      throw new Error(`Missing required CSV header: ${header}`);
    }
  }

  return lines.slice(1).map((line, index) => {
    const values = parseDelimitedLine(line);
    return {
      lineNumber: index + 2,
      name: values[headerIndexes.Name] ?? "",
      email: (values[headerIndexes["E-Mail Adresse"]] ?? "").trim().toLowerCase(),
      roleLabel: values[headerIndexes.Rolle] ?? "",
      operationalArea: values[headerIndexes["Operativer Bereich"]] ?? "",
    };
  });
}

export function buildAreaDefaults(
  rows: CsvUserRow[],
  organizations: OrganizationOption[]
) {
  const organizationLookup = new Map<string, string>();

  for (const organization of organizations) {
    organizationLookup.set(normalizeForLookup(organization.name), organization._id);
    if (organization.slug) {
      organizationLookup.set(normalizeForLookup(organization.slug), organization._id);
    }
  }

  const defaults: Record<string, string> = {};
  const uniqueAreas = new Set<string>();

  for (const row of rows) {
    for (const area of splitBusinessAreas(row.operationalArea)) {
      uniqueAreas.add(area);
    }
  }

  for (const area of uniqueAreas) {
    const match = organizationLookup.get(normalizeForLookup(area));
    if (match) {
      defaults[area] = match;
    }
  }

  return defaults;
}

type BuildImportPreviewArgs = {
  rows: CsvUserRow[];
  organizations: OrganizationOption[];
  existingEmails: Set<string>;
  areaAssignments: Record<string, string>;
};

export function buildImportPreview(args: BuildImportPreviewArgs): ImportPreviewRow[] {
  const organizationById = new Map(
    args.organizations.map((organization) => [organization._id, organization])
  );
  const fileEmailCounts = args.rows.reduce<Map<string, number>>((acc, row) => {
    acc.set(row.email, (acc.get(row.email) ?? 0) + 1);
    return acc;
  }, new Map());

  const seenEmails = new Set<string>();

  return args.rows.map((row) => {
    const email = row.email.trim().toLowerCase();
    const role = normalizeCsvRole(row.roleLabel);
    const businessAreas = splitBusinessAreas(row.operationalArea);
    const nameParts = splitName(row.name);

    let status: ImportRowStatus = "ready";
    let reason: ImportRowReason | null = null;
    let organizationId: string | null = null;
    let organizationName: string | null = null;

    if (!email || !email.includes("@")) {
      status = "invalid";
      reason = "invalid_email";
    } else if (!nameParts) {
      status = "invalid";
      reason = "invalid_name";
    } else if (role === null) {
      status = "skipped";
      reason = "unsupported_role";
    } else if (businessAreas.length === 0) {
      status = "invalid";
      reason = "missing_area";
    } else if (businessAreas.length > 1) {
      status = "invalid";
      reason = "multiple_areas";
    } else {
      organizationId = args.areaAssignments[businessAreas[0]] ?? null;
      organizationName = organizationId
        ? organizationById.get(organizationId)?.name ?? null
        : null;

      if (!organizationId) {
        status = "invalid";
        reason = "missing_area_mapping";
      } else if (args.existingEmails.has(email)) {
        status = "invalid";
        reason = seenEmails.has(email) ? "duplicate_email" : "existing_email";
      } else if ((fileEmailCounts.get(email) ?? 0) > 1 && seenEmails.has(email)) {
        status = "invalid";
        reason = "duplicate_email";
      }
    }

    if (email) {
      seenEmails.add(email);
    }

    return {
      lineNumber: row.lineNumber,
      name: row.name,
      email,
      firstName: nameParts?.firstName ?? "",
      lastName: nameParts?.lastName ?? "",
      role,
      roleLabel: row.roleLabel,
      status,
      reason,
      operationalArea: row.operationalArea,
      businessAreas,
      organizationId,
      organizationName,
    };
  });
}
